import { PrimaryButton, ReportLoading, ReportResult, SpamReportResult } from '@components';
import { paths, v2PageBackground } from '@constants';
import { makeStyles, Text } from '@fluentui/react-components';
import { useAutofocus } from '@utils';
import { NewEmailIcon, NewLinkIcon, NewPhishingIcon, NewSpamIcon } from '@utils-global';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useConfigurationStorage, useIsV2 } from '@data';
import { useSecureBeforeYouClick, useSpamReport, useTestLinks } from './hooks';

const useStyles = makeStyles({
  page: {
    padding: '20px 30px',
    background: v2PageBackground,
    minHeight: '100vh',
    width: '100%',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    '&:not(:first-child)': {
      marginTop: '40px',
    },
  },
  title: {
    color: '#ffffff',
  },
});

export const HomePage = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { targetRef } = useAutofocus();
  const { isTestLinksPending, handleTestLinks } = useTestLinks();
  const { isSecureBeforeYouClickPending, handleSecureBeforeYouClick } = useSecureBeforeYouClick();
  const {
    isLoading: isSpamReportLoading,
    report: spamReport,
    handleReport: handleSpamReport,
    resetReport: resetSpamReport,
  } = useSpamReport();
  const { configuration } = useConfigurationStorage();
  const isV2 = useIsV2();

  // Show loading when spam report is in progress
  if (isSpamReportLoading) return <ReportLoading />;

  // Show result when spam report is complete
  if (spamReport) {
    if (isV2) return <SpamReportResult />;
    return <ReportResult report={spamReport} onBack={resetSpamReport} />;
  }

  return (
    <section aria-label="Home page" className={styles.page} tabIndex={-1} ref={targetRef}>
      {(configuration?.data?.['secure-before-you-click'] ||
        configuration?.data?.['test-safely']) && (
        <div className={styles.wrapper}>
          <Text size={500} weight="semibold" role="heading" aria-level={2} className={styles.title}>
            {t('homePage.secure.title')}
          </Text>
          {configuration?.data?.['secure-before-you-click'] && (
            <PrimaryButton
              onClick={handleSecureBeforeYouClick}
              title={t('homePage.secure.subtitle')}
              isLoading={isSecureBeforeYouClickPending}
              description={t('homePage.secure.description')}
              icon={<NewEmailIcon aria-hidden="true" />}
            />
          )}
          {configuration?.data?.['test-safely'] && (
            <PrimaryButton
              onClick={handleTestLinks}
              isLoading={isTestLinksPending}
              title={t('homePage.test.subtitle')}
              description={t('homePage.test.description')}
              icon={<NewLinkIcon aria-hidden="true" />}
            />
          )}
        </div>
      )}
      <div className={styles.wrapper}>
        <Text size={500} weight="semibold" role="heading" aria-level={2} className={styles.title}>
          {t('homePage.report.title')}
        </Text>
        <Link to={paths.report}>
          <PrimaryButton
            title={t('homePage.report.phishing.title')}
            description={t('homePage.report.phishing.description')}
            icon={<NewPhishingIcon aria-hidden="true" />}
          />
        </Link>
        <PrimaryButton
          onClick={handleSpamReport}
          title={t('homePage.report.spam.title')}
          description={t('homePage.report.spam.description')}
          icon={<NewSpamIcon aria-hidden="true" />}
        />
      </div>
    </section>
  );
};
